import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const faqs = [
  { question: "What does the assistant actually do?", answer: "It autonomously manages your inbox, scheduling, meeting preparation, and basic task follow-ups. Unlike a standalone chatbot, it operates in the background — reading context across your tools and proposing actions for your approval." },
  { question: "What do I actually need to do?", answer: "During 'Week One', you simply interact with it. When it drafts an email, you edit or approve it. When it suggests a meeting shift, you confirm or decline. Every correction trains its underlying model on your specific preferences, voice, and priorities." },
  { question: "How quickly can I be up and running?", answer: "From the moment you complete your kickoff call, standard deployment takes 48 hours. Enterprise and on-premise deployments take slightly longer based on hardware provisioning and IT security audits." },
  { question: "Is my data safe?", answer: "Yes. We use enterprise-grade encryption and standard OAuth protocols. Your assistant runs on isolated, dedicated infrastructure. For Premium clients, we offer completely air-gapped local hardware options where your data literally never leaves your building." },
  { question: "What happens after the first 30 days?", answer: "The assistant is fully trained and runs autonomously on your infrastructure. Because we price this as infrastructure rather than a SaaS subscription, there are no ongoing monthly fees to keep it running." },
  { question: "Can multiple people on my team each get one?", answer: "Absolutely. Our Enterprise plans support deploying distinct assistants for different roles. Each assistant learns the specific communication style and priorities of its assigned executive." },
];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <div className={`border rounded-xl overflow-hidden mb-3 transition-all duration-200 ${isOpen ? 'border-[#B8CDD9] bg-[#EBF2F9]/40' : 'border-[#E4DFD8] bg-white hover:border-[#D4CFC8]'}`}>
    <button className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" onClick={onToggle}>
      <span className="text-[15px] font-semibold text-[#1A1A18] pr-8 leading-snug">{faq.question}</span>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${isOpen ? 'bg-[#2A5C8A] text-white rotate-180' : 'bg-[#F0EDE8] text-[#6B6560]'}`}>
        <SafeIcon icon={FiIcons.FiChevronDown} className="text-sm" />
      </div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
        >
          <div className="px-6 pb-5 text-[#6B6560] text-sm leading-relaxed border-t border-[#E4DFD8] pt-4">
            {faq.answer}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faq" className="py-32 bg-[#F7F5F2]">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="tag-pill mx-auto mb-5 w-fit">Common Questions</div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#1A1A18] tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Clear answers.
          </h2>
        </div>
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          {faqs.map((faq, idx) => (
            <FAQItem key={idx} faq={faq} isOpen={openIdx === idx} onToggle={() => setOpenIdx(openIdx === idx ? -1 : idx)} />
          ))}
        </motion.div>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="mt-10 text-center">
          <p className="text-[#6B6560] text-sm">
            Have a specific workflow in mind?{' '}
            <a href="#" className="text-[#2A5C8A] hover:text-[#1E4D7A] font-medium underline underline-offset-4">Let's talk on an intro call.</a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;